import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { BASE_API_URL } from "../../utils/constants";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Paper,
  TextField,
  InputAdornment,
  CircularProgress,
  Alert,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { DataGrid } from "@mui/x-data-grid";
import "./GenericHubReport.css";

const GenericHubReport = ({ hub }) => {
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  useEffect(() => {
    const fetchReport = async () => {
      try {
        setLoading(true);
        setError("");
        setReportData(null);

        const res = await axios.get(`${BASE_API_URL}/powerBI/hub-report`, {
          params: { hub },
        });

        setReportData(res.data);
      } catch (err) {
        setError("Failed to load report");
      } finally {
        setLoading(false);
      }
    };

    fetchReport();

    setSearch("");
    setPaginationModel({ page: 0, pageSize: 10 });
  }, [hub]);

  const ownerEntityBreakdown = reportData?.ownerEntityBreakdown || [];

  const tableRows = useMemo(
    () =>
      ownerEntityBreakdown
        .filter((item) =>
          item.entity?.toLowerCase().includes(search.toLowerCase()),
        )
        .map((item, index) => ({
          id: index + 1,
          sn: index + 1,
          ...item,
        })),
    [ownerEntityBreakdown, search],
  );

  const totalUsers = reportData?.totalLearners || 0;

  const totalPages =
    Math.ceil(tableRows.length / paginationModel.pageSize) || 1;
  const currentPage = paginationModel.page + 1;
  const visiblePages = Array.from(
    { length: totalPages },
    (_, i) => i + 1,
  ).filter(
    (page) =>
      page === 1 || page === totalPages || Math.abs(page - currentPage) <= 2,
  );

  const renderStatusCell = (params) =>
    params.value === null || params.value === undefined ? "N/A" : params.value;

  const columns = [
    { field: "sn", headerName: "S.N", flex: 0.5, minWidth: 70 },
    { field: "entity", headerName: "Entity", flex: 1.5, minWidth: 180 },
    { field: "owner", headerName: "Owner", flex: 1.4, minWidth: 180 },
    {
      field: "totalLearners",
      headerName: "Total Learners",
      flex: 1,
      minWidth: 140,
    },
    {
      field: "completed",
      headerName: "Completed",
      flex: 1,
      minWidth: 120,
      renderCell: renderStatusCell,
    },
    {
      field: "inProgress",
      headerName: "In Progress",
      flex: 1,
      minWidth: 120,
      renderCell: renderStatusCell,
    },
    {
      field: "notStarted",
      headerName: "Not Started",
      flex: 1,
      minWidth: 120,
      renderCell: renderStatusCell,
    },
  ];

  const totalLearners = reportData?.totalLearners ?? 0;

  const hasMandatoryMetric =
    reportData?.hasMandatoryCourses &&
    reportData?.summary?.completed !== null &&
    reportData?.summary?.completed !== undefined;

  const mandatoryCompletionRate =
    hasMandatoryMetric && totalLearners > 0
      ? Math.round((reportData.summary.completed / totalLearners) * 100)
      : null;

  const mandatoryInProgressRate =
    hasMandatoryMetric && totalLearners > 0
      ? Math.round((reportData.summary.inProgress / totalLearners) * 100)
      : null;

  const hasEngagementMetric =
    reportData &&
    reportData.hasMandatoryCourses === false &&
    reportData?.summary?.engagementCompleted !== null &&
    reportData?.summary?.engagementCompleted !== undefined;

  const engagementCompletionRate =
    hasEngagementMetric && totalLearners > 0
      ? Math.round(
          (reportData.summary.engagementCompleted / totalLearners) * 100,
        )
      : null;

  const summaryCards = reportData?.hasMandatoryCourses
    ? [
        hasMandatoryMetric
          ? {
              title: "Mandatory Completion",
              value: reportData.summary.completed,
              percent: mandatoryCompletionRate,
              subtitle: `Total completion out of ${totalLearners} Participants`,
              gradient: "linear-gradient(90deg, #E09563, #FCA750)",
            }
          : {
              title: "Mandatory Completion",
              value: "N/A",
              percent: null,
              subtitle: "This hub has no mandatory courses",
              gradient: "linear-gradient(90deg, #8a8a8a, #b5b5b5)",
            },
        hasMandatoryMetric
          ? {
              title: "Mandatory In Progress",
              value: reportData.summary.inProgress,
              percent: mandatoryInProgressRate,
              subtitle:
                "Learners currently progressing through mandatory courses",
              gradient: "linear-gradient(90deg, #4D5FAA, #5773EF)",
            }
          : {
              title: "Mandatory In Progress",
              value: "N/A",
              percent: null,
              subtitle: "This hub has no mandatory courses",
              gradient: "linear-gradient(90deg, #8a8a8a, #b5b5b5)",
            },
      ]
    : [
        hasEngagementMetric
          ? {
              title: "Course Engagement — Completed",
              value: reportData.summary.engagementCompleted,
              percent: engagementCompletionRate,
              subtitle: `All ${totalLearners} active learners — this hub has no mandatory course requirement`,
              gradient: "linear-gradient(90deg, #E09563, #FCA750)",
            }
          : {
              title: "Course Engagement — Completed",
              value: "N/A",
              percent: null,
              subtitle: "No course data available",
              gradient: "linear-gradient(90deg, #8a8a8a, #b5b5b5)",
            },
      ];

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
          gap: 2,
        }}
      >
        <CircularProgress size={55} thickness={4} />
        <Typography variant="h6" color="text.secondary">
          Loading Learners Report...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <Alert severity="error" sx={{ width: 420, fontSize: "16px" }}>
          {error}
        </Alert>
      </Box>
    );
  }

  return (
    <div className="genericHubReport">
      <Typography variant="h5" className="sectionTitle">
        Overall Learning Completion Summery
      </Typography>

      <div className="summaryGrid">
        {summaryCards.map((card) => (
          <Card
            key={card.title}
            className="summaryCard"
            sx={{ background: card.gradient }}
          >
            <CardContent className="summaryCardContent">
              <div className="summaryCardLeft">
                <div className="summaryIconWrapper">
                  <img
                    src={`${process.env.PUBLIC_URL}/Assets/foundation-icon.svg`}
                    alt={card.title}
                    className="summary-card-icon"
                  />
                </div>
                <Typography className="cardTitle">{card.title}</Typography>
              </div>

              <div className="summaryCardRight">
                <Typography className="cardNumber">{card.value}</Typography>
                <Typography className="cardSubtext">{card.subtitle}</Typography>
                {card.percent !== null && (
                  <Typography className="cardPercent">
                    {card.percent}%
                  </Typography>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      <Paper className="tablePaper">
        <Box className="tableHeader">
          <Box className="tableHeaderLeft">
            <Typography variant="h6" className="tableTitle">
              Learner Completion Details
            </Typography>
            <Typography className="totalUsers" variant="h6">
              <strong>Total Users :</strong>
              <span className="totalUsersCount">{totalUsers}</span>
            </Typography>
          </Box>

          <Box className="tableFilters">
            <TextField
              size="small"
              placeholder="Search Entity"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
            />
          </Box>
        </Box>

        <div style={{ width: "100%", height: 650 }}>
          <DataGrid
            rows={tableRows}
            columns={columns}
            pageSizeOptions={[10, 20, 50]}
            disableRowSelectionOnClick
            paginationModel={paginationModel}
            onPaginationModelChange={setPaginationModel}
            hideFooter
            rowHeight={52}
            density="standard"
            sx={{
              border: 0,
              "& .MuiDataGrid-columnHeaders": {
                background: "#5b2ca0",
                color: "#fff",
                fontWeight: 700,
              },
              "& .MuiDataGrid-row:hover": {
                background: "#f5f5ff",
              },
            }}
          />
        </div>
      </Paper>

      <div className="customPagination">
        <div className="pageNumbers">
          <button
            disabled={paginationModel.page === 0}
            onClick={() =>
              setPaginationModel((prev) => ({ ...prev, page: prev.page - 1 }))
            }
          >
            &lt;
          </button>

          {visiblePages.map((page) => (
            <button
              key={page}
              className={page === currentPage ? "activePage" : ""}
              onClick={() =>
                setPaginationModel((prev) => ({ ...prev, page: page - 1 }))
              }
            >
              {page}
            </button>
          ))}

          <button
            disabled={paginationModel.page === totalPages - 1}
            onClick={() =>
              setPaginationModel((prev) => ({ ...prev, page: prev.page + 1 }))
            }
          >
            &gt;
          </button>
        </div>

        <div className="itemsPerPage">
          <span>Items per page</span>
          <select
            value={paginationModel.pageSize}
            onChange={(e) =>
              setPaginationModel({ page: 0, pageSize: Number(e.target.value) })
            }
          >
            <option value={10}>10</option>
            <option value={20}>20</option>
            <option value={50}>50</option>
          </select>
        </div>
      </div>
    </div>
  );
};

export default GenericHubReport;
